import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Plus, Save, X, Edit2, Trash2, User, Phone, Mail, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

type ContactKind = 'email' | 'phone';
type GuardianRelationship = 'mother' | 'father' | 'foster_parent' | 'emergency' | 'other';

interface GuardianContact {
  id: number;
  entity_type: 'guardian';
  entity_ref: string;
  relationship: GuardianRelationship | null;
  display_name: string | null;
  kind: ContactKind;
  value: string;
  is_primary: boolean;
  consent_status: 'unknown' | 'granted' | 'revoked';
  created_at: string;
  updated_at: string;
}

interface StudentGuardianContactsManagerProps {
  studentUnid: string;
  studentName: string;
}

interface GuardianContactsResponse {
  data: GuardianContact[];
  meta?: {
    capabilities?: {
      supportsPhoneKind?: boolean;
      hasRelationship?: boolean;
      hasDisplayName?: boolean;
    };
  };
}

const relationshipOptions: Array<{ value: GuardianRelationship; label: string }> = [
  { value: 'mother', label: 'Mutter' },
  { value: 'father', label: 'Vater' },
  { value: 'foster_parent', label: 'Pflegeeltern' },
  { value: 'emergency', label: 'Notfallkontakt' },
  { value: 'other', label: 'Sonstige' },
];

const kindOptions: Array<{ value: ContactKind; label: string }> = [
  { value: 'email', label: 'E-Mail' },
  { value: 'phone', label: 'Telefon' },
];

interface FormState {
  displayName: string;
  relationship: GuardianRelationship;
  kind: ContactKind;
  value: string;
  isPrimary: boolean;
}

const emptyForm: FormState = {
  displayName: '',
  relationship: 'other',
  kind: 'email',
  value: '',
  isPrimary: false,
};

export default function StudentGuardianContactsManager({ studentUnid, studentName }: StudentGuardianContactsManagerProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const queryClient = useQueryClient();

  const [supportsPhoneKind, setSupportsPhoneKind] = useState(true);
  const { data: contacts = [], isLoading, error } = useQuery<GuardianContact[]>(
    ['guardian-contacts', studentUnid],
    async () => {
      const response = await api.get<GuardianContactsResponse>(`/contacts/student/${studentUnid}/guardians`);
      const capabilities = response.data?.meta?.capabilities;
      setSupportsPhoneKind(capabilities?.supportsPhoneKind !== false);
      return response.data?.data || [];
    },
    { enabled: !!studentUnid, retry: 2 }
  );

  const nextKontaktName = useMemo(() => {
    let maxIndex = 0;
    contacts.forEach((contact) => {
      const match = String(contact.display_name || '').match(/^Kontakt(\d+)$/i);
      if (match?.[1]) {
        const idx = Number(match[1]);
        if (Number.isFinite(idx) && idx > maxIndex) maxIndex = idx;
      }
    });
    return `Kontakt${maxIndex + 1}`;
  }, [contacts]);

  const resetCreateForm = () => {
    setCreateForm((prev) => ({ ...emptyForm, displayName: prev.displayName || nextKontaktName }));
  };

  const addMutation = useMutation(
    async (payload: FormState) => api.post(`/contacts/student/${studentUnid}/guardian-contact`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['guardian-contacts', studentUnid]);
        setIsAdding(false);
        setCreateForm(emptyForm);
        toast.success('Kontakt hinzugefügt');
      },
      onError: (error: any) => {
        const validationMessage = error?.response?.data?.details?.[0]?.msg;
        const message = validationMessage || error?.response?.data?.error || 'Fehler beim Hinzufügen des Kontakts';
        toast.error(message);
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }: { id: number; payload: Partial<FormState> }) => api.put(`/contacts/${id}`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['guardian-contacts', studentUnid]);
        setEditingId(null);
        setEditForm(emptyForm);
        toast.success('Kontakt aktualisiert');
      },
      onError: (error: any) => {
        const validationMessage = error?.response?.data?.details?.[0]?.msg;
        const message = validationMessage || error?.response?.data?.error || 'Fehler beim Aktualisieren des Kontakts';
        toast.error(message);
      }
    }
  );

  const deleteMutation = useMutation(
    async (id: number) => api.delete(`/contacts/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['guardian-contacts', studentUnid]);
        toast.success('Kontakt gelöscht');
      },
      onError: (error: any) => {
        const message = error?.response?.data?.error || 'Fehler beim Löschen des Kontakts';
        toast.error(message);
      }
    }
  );

  const openAdd = () => {
    setCreateForm({ ...emptyForm, displayName: nextKontaktName });
    setIsAdding(true);
  };

  const handleAdd = () => {
    if (!createForm.value.trim()) {
      toast.error('Bitte einen Wert eintragen');
      return;
    }
    if (createForm.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.value.trim())) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }
    if (createForm.kind === 'phone' && !/^[+()\-.\s\d]{6,30}$/.test(createForm.value.trim())) {
      toast.error('Bitte eine gültige Telefonnummer eingeben');
      return;
    }
    if (createForm.kind === 'phone' && !supportsPhoneKind) {
      toast.error('Telefonkontakte benötigen die aktuelle DB-Migration (contact_points.kind mit phone).');
      return;
    }
    addMutation.mutate({
      ...createForm,
      displayName: createForm.displayName.trim() || nextKontaktName,
      value: createForm.value.trim(),
    });
  };

  const startEdit = (contact: GuardianContact) => {
    setEditingId(contact.id);
    setEditForm({
      displayName: contact.display_name || '',
      relationship: (contact.relationship || 'other') as GuardianRelationship,
      kind: contact.kind,
      value: contact.value,
      isPrimary: contact.is_primary,
    });
  };

  const saveEdit = (id: number) => {
    if (!editForm.value.trim()) {
      toast.error('Bitte einen Wert eintragen');
      return;
    }
    if (editForm.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.value.trim())) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }
    if (editForm.kind === 'phone' && !/^[+()\-.\s\d]{6,30}$/.test(editForm.value.trim())) {
      toast.error('Bitte eine gültige Telefonnummer eingeben');
      return;
    }
    if (editForm.kind === 'phone' && !supportsPhoneKind) {
      toast.error('Telefonkontakte benötigen die aktuelle DB-Migration (contact_points.kind mit phone).');
      return;
    }
    updateMutation.mutate({
      id,
      payload: {
        ...editForm,
        displayName: editForm.displayName.trim(),
        value: editForm.value.trim(),
      }
    });
  };

  const roleLabel = (value: GuardianRelationship | null) =>
    relationshipOptions.find((opt) => opt.value === (value || 'other'))?.label || 'Sonstige';

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-300">Lade Kontakte...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-white p-4 dark:border-red-800 dark:bg-gray-900">
        <div className="text-red-600">Fehler beim Laden der Kontakte</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center text-lg font-semibold text-gray-800 dark:text-gray-100">
          <User className="h-5 w-5 mr-2 text-blue-600" />
          Erziehungs- und Notfallkontakte
        </h3>
        <button onClick={openAdd} disabled={isAdding} className="btn btn-primary btn-sm flex items-center">
          <Plus className="h-4 w-4 mr-1" />
          Hinzufügen
        </button>
      </div>

      {isAdding && (
        <div className="mb-4 grid grid-cols-1 gap-2 rounded border border-blue-200 bg-blue-50 p-3 dark:border-gray-700 dark:bg-gray-800 md:grid-cols-5">
          <input
            className="input input-sm border-gray-300"
            placeholder="Name (z. B. Kontakt1)"
            value={createForm.displayName}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, displayName: e.target.value }))}
          />
          <select
            className="select select-sm border-gray-300"
            value={createForm.relationship}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, relationship: e.target.value as GuardianRelationship }))}
          >
            {relationshipOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            className="select select-sm border-gray-300"
            value={createForm.kind}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, kind: e.target.value as ContactKind }))}
          >
            {kindOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            className="input input-sm border-gray-300"
            placeholder={createForm.kind === 'email' ? 'E-Mail-Adresse' : 'Telefonnummer'}
            value={createForm.value}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, value: e.target.value }))}
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs dark:text-gray-300">
              <input
                type="checkbox"
                checked={createForm.isPrimary}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, isPrimary: e.target.checked }))}
              />
              Primär
            </label>
            <button onClick={handleAdd} className="btn btn-primary btn-sm"><Save className="h-4 w-4" /></button>
            <button
              onClick={() => {
                setIsAdding(false);
                resetCreateForm();
              }}
              className="btn btn-secondary btn-sm"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {contacts.length === 0 ? (
          <div className="py-6 text-center text-gray-500 dark:text-gray-400">Keine Kontakte vorhanden</div>
        ) : (
          contacts.map((contact) => (
            <div
              key={contact.id}
              className={`rounded border p-3 ${
                contact.is_primary
                  ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                  : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
              }`}
            >
              {editingId === contact.id ? (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  <input
                    className="input input-sm border-gray-300"
                    value={editForm.displayName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  />
                  <select
                    className="select select-sm border-gray-300"
                    value={editForm.relationship}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, relationship: e.target.value as GuardianRelationship }))}
                  >
                    {relationshipOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <select
                    className="select select-sm border-gray-300"
                    value={editForm.kind}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, kind: e.target.value as ContactKind }))}
                  >
                    {kindOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <input
                    className="input input-sm border-gray-300"
                    value={editForm.value}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, value: e.target.value }))}
                  />
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={editForm.isPrimary}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, isPrimary: e.target.checked }))}
                      />
                      Primär
                    </label>
                    <button onClick={() => saveEdit(contact.id)} className="btn btn-primary btn-sm"><Save className="h-4 w-4" /></button>
                    <button onClick={() => setEditingId(null)} className="btn btn-secondary btn-sm"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                      {contact.kind === 'email' ? <Mail className="h-4 w-4 text-blue-600" /> : <Phone className="h-4 w-4 text-blue-600" />}
                      <span>{contact.display_name || 'Kontakt'}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({roleLabel(contact.relationship)})</span>
                      {contact.is_primary && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                    </div>
                    <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">{contact.value}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!contact.is_primary && (
                      <button
                        className="btn btn-secondary btn-sm"
                        title="Als primär markieren"
                        onClick={() => updateMutation.mutate({ id: contact.id, payload: { isPrimary: true } })}
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" title="Bearbeiten" onClick={() => startEdit(contact)}>
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      title="Löschen"
                      onClick={() => {
                        if (window.confirm('Kontakt wirklich löschen?')) deleteMutation.mutate(contact.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-4 rounded bg-gray-50 p-2 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        <strong>Hinweis:</strong> Diese Kontakte gelten für die Kommunikation zu {studentName}.
      </div>
    </div>
  );
}
